import { useState, useCallback, useEffect } from 'react';

export type UseModelSelectorOptions<T extends string> = {
  /** localStorage key for persisting the selection. */
  storageKey: string;
  /** Value to use when no valid stored value exists. */
  defaultValue: T;
  /** Allowed values — used to validate stored and incoming values. */
  validValues: T[];
};

export type UseModelSelectorResult<T extends string> = {
  value: T;
  setValue: (value: T) => void;
};

const MODEL_SELECTOR_EVENT = 'triedge:model-selector-change';

function readStoredValue<T extends string>(
  storageKey: string,
  validValues: T[],
  defaultValue: T,
): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored != null && (validValues as string[]).includes(stored)) {
      return stored as T;
    }
  } catch {
    // localStorage may throw in private browsing or when full
  }
  return defaultValue;
}

export function useModelSelector<T extends string>(
  options: UseModelSelectorOptions<T>,
): UseModelSelectorResult<T> {
  const { storageKey, defaultValue, validValues } = options;

  const [value, setValueInternal] = useState<T>(
    () => readStoredValue(storageKey, validValues, defaultValue),
  );

  const setValue = useCallback(
    (next: T) => {
      if (!(validValues as string[]).includes(next)) {
        return;
      }
      setValueInternal(next);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKey, next);
          window.dispatchEvent(new CustomEvent(MODEL_SELECTOR_EVENT, {
            detail: {
              storageKey,
              value: next,
            },
          }));
        } catch {
          // Silently ignore storage failures
        }
      }
    },
    [storageKey, validValues],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncFromStorage = (nextValue: string | null) => {
      if (nextValue != null && (validValues as string[]).includes(nextValue)) {
        setValueInternal(nextValue as T);
        return;
      }
      setValueInternal(defaultValue);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) {
        return;
      }
      syncFromStorage(event.newValue);
    };

    const handleModelSelectorEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ storageKey?: string; value?: string | null }>).detail;
      if (!detail || detail.storageKey !== storageKey) {
        return;
      }
      syncFromStorage(detail.value ?? null);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(MODEL_SELECTOR_EVENT, handleModelSelectorEvent as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(MODEL_SELECTOR_EVENT, handleModelSelectorEvent as EventListener);
    };
  }, [defaultValue, storageKey, validValues]);

  return { value, setValue };
}
